export type PalletFrontShare = {
  label: string;
  /** Número del producto en la lista de la estimación. */
  index: number | null;
  /** Parte del pallet que ocupa este producto (1 = pallet completo). */
  fraction: number;
  layers: number | null;
  boxesPerLayer: number | null;
  stackable: boolean | null;
  unitsPerPallet?: number | null;
  /** Largo y alto cargado del pallet (mm): permiten dibujar la proporción real de la caja. */
  palletLengthMm?: number | null;
  palletHeightMm?: number | null;
  palletWidthMm?: number | null;
};

export type PalletFrontSlot = { shares: PalletFrontShare[] };

type Props = {
  slots: PalletFrontSlot[];
  selectedIndex: number | null;
  t: (key: string, options?: Record<string, unknown>) => string;
};

const VIEW_W = 170;
const PALLET_X = 10;
const PALLET_W = 150;
const DECK_Y = 132;
const DEFAULT_LAYERS = 3;
const MAX_SLOTS = 12;
const PALLET_BASE_MM = 144;
const DEFAULT_BOX_ASPECT = 0.78;

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

const EUR_LENGTH_MM = 1200;
const EUR_WIDTH_MM = 800;

/**
 * Cómo se reparten las cajas de un piso sobre el pallet: `across` a lo largo (lo que se ve de frente) por
 * `deep` en profundidad. Se elige la distribución que deja la caja más proporcionada y sin huecos.
 */
function arrangeLayer(share: PalletFrontShare): { across: number; deep: number } {
  const count = Math.round(share.boxesPerLayer ?? 0);
  if (count <= 0) return { across: 2, deep: 1 };
  const length = share.palletLengthMm || EUR_LENGTH_MM;
  const width = share.palletWidthMm || EUR_WIDTH_MM;
  let best = { across: count, deep: 1, score: Number.POSITIVE_INFINITY };
  for (let deep = 1; deep <= 6; deep += 1) {
    const across = Math.ceil(count / deep);
    const boxW = length / across;
    const boxD = width / deep;
    const aspect = Math.max(boxW, boxD) / Math.min(boxW, boxD);
    const score = aspect + 3 * (across * deep - count);
    if (score < best.score) best = { across, deep, score };
  }
  return { across: clamp(best.across, 1, 12), deep: best.deep };
}

function CartonBox({ x, y, w, h, label, dim }: { x: number; y: number; w: number; h: number; label: number | null; dim: boolean }) {
  const tapeW = Math.max(3, w * 0.16);
  const showSymbols = w > 26 && h > 14;
  return (
    <g opacity={dim ? 0.3 : 1}>
      <rect x={x} y={y} width={w} height={h} rx="1.2" fill="url(#palletBoxGrad)" stroke="#8a5f30" strokeOpacity="0.7" strokeWidth="0.7" />
      <rect x={x + w / 2 - tapeW / 2} y={y} width={tapeW} height={h} fill="#a9763c" fillOpacity="0.55" />
      <rect x={x} y={y} width={w} height={Math.min(3, h * 0.18)} fill="#ffffff" fillOpacity="0.18" />
      {showSymbols ? (
        <g fill="none" stroke="#6b4723" strokeOpacity="0.8" strokeWidth="0.7" strokeLinecap="round" strokeLinejoin="round">
          <path d={`M ${x + w - 17} ${y + 8} v -5 m -1.6 1.8 l 1.6 -1.8 l 1.6 1.8`} />
          <path d={`M ${x + w - 10} ${y + 8} q 3 -6 6 0 z`} />
        </g>
      ) : null}
      {label != null && w > 14 && h > 9 ? (
        <text x={x + 3} y={y + Math.min(h - 3, 10)} fontSize="7.5" fontWeight="800" fill="#4a3219" stroke="#f6e7cc" strokeWidth="1.6" paintOrder="stroke">{label}</text>
      ) : null}
    </g>
  );
}

function PalletWood() {
  return (
    <g stroke="#6f4b25" strokeOpacity="0.7" strokeWidth="0.7">
      <rect x={PALLET_X} y={DECK_Y} width={PALLET_W} height="5" fill="#b8834d" />
      <rect x={PALLET_X + 6} y={DECK_Y + 5} width="16" height="9" fill="#9a6a3a" />
      <rect x={PALLET_X + PALLET_W / 2 - 8} y={DECK_Y + 5} width="16" height="9" fill="#9a6a3a" />
      <rect x={PALLET_X + PALLET_W - 22} y={DECK_Y + 5} width="16" height="9" fill="#9a6a3a" />
      <rect x={PALLET_X} y={DECK_Y + 14} width={PALLET_W} height="3" fill="#b8834d" />
    </g>
  );
}

/** Proporción alto/ancho de la cara frontal de la caja, a partir de las medidas del pallet y su cadena. */
function boxAspect(share: PalletFrontShare, columns: number, layers: number) {
  const { palletLengthMm: length, palletHeightMm: height } = share;
  if (!length || !height || height <= PALLET_BASE_MM || layers < 1) return DEFAULT_BOX_ASPECT;
  const boxHeightMm = (height - PALLET_BASE_MM) / layers;
  const boxWidthMm = length / columns;
  return clamp(boxHeightMm / boxWidthMm, 0.25, 2.5);
}

function layoutSlot(slot: PalletFrontSlot) {
  const mixed = slot.shares.length > 1 || slot.shares[0].fraction < 0.995;
  // Cada producto conserva su propia caja: tamaño, cajas por piso y pisos salen de su maestro. En un pallet
  // compartido cada tramo ocupa los pisos que le corresponden por su parte del pallet (de mayor a menor).
  const tiers = slot.shares.map((share) => {
    const { across, deep } = arrangeLayer(share);
    const layers = clamp(Math.round(share.layers ?? DEFAULT_LAYERS), 1, 12);
    const rows = mixed ? clamp(Math.round(layers * share.fraction), 1, layers) : layers;
    const boxW = PALLET_W / across;
    return { share, rows, columns: across, deep, boxW, boxH: boxW * boxAspect(share, across, layers) };
  });
  const stackH = tiers.reduce((sum, tier) => sum + tier.rows * tier.boxH, 0);
  // Si las cajas son grandes la pila crece y el encuadre se amplía hacia arriba (nunca se aplasta la caja).
  const viewTop = Math.min(0, DECK_Y - stackH - 6);
  return { tiers, viewTop, viewH: DECK_Y + 18 - viewTop };
}

/** Encuadre (viewBox) del dibujo frontal; su alto depende de lo altas que sean las cajas. */
export function palletFrontView(slot: PalletFrontSlot) {
  const { viewTop, viewH } = layoutSlot(slot);
  return { viewBox: `0 ${viewTop} ${VIEW_W} ${viewH}`, aspect: viewH / VIEW_W };
}

export function PalletFrontGraphic({ slot, selectedIndex }: { slot: PalletFrontSlot; selectedIndex: number | null }) {
  const { tiers } = layoutSlot(slot);
  const baseY = DECK_Y;
  const stackX = PALLET_X;

  let usedHeight = 0;
  const boxes: JSX.Element[] = [];
  tiers.forEach((tier, tierIndex) => {
    for (let row = 0; row < tier.rows; row += 1) {
      const y = baseY - usedHeight - (row + 1) * tier.boxH;
      for (let col = 0; col < tier.columns; col += 1) {
        boxes.push(
          <CartonBox
            key={`b-${tierIndex}-${row}-${col}`}
            x={stackX + col * tier.boxW}
            y={y}
            w={tier.boxW}
            h={tier.boxH}
            label={tier.share.index}
            dim={selectedIndex != null && tier.share.index !== selectedIndex}
          />,
        );
      }
    }
    usedHeight += tier.rows * tier.boxH;
  });

  return (
    <>
      <defs>
        <linearGradient id="palletBoxGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#c8985a" />
          <stop offset="0.5" stopColor="#dcb47c" />
          <stop offset="1" stopColor="#c8985a" />
        </linearGradient>
      </defs>
      <ellipse cx={VIEW_W / 2} cy={DECK_Y + 16} rx={PALLET_W / 2} ry="3" fill="#0f172a" fillOpacity="0.12" />
      {boxes}
      <PalletWood />
    </>
  );
}

/** Planta de un piso: cuántas cajas hay por piso y cómo se reparten (largo × profundidad). */
function LayerPlan({ share }: { share: PalletFrontShare }) {
  const { across, deep } = arrangeLayer(share);
  const count = Math.round(share.boxesPerLayer ?? 0);
  const W = 56;
  const H = (W * (share.palletWidthMm || EUR_WIDTH_MM)) / (share.palletLengthMm || EUR_LENGTH_MM);
  const cellW = W / across;
  const cellH = H / deep;
  return (
    <svg viewBox={`-1 -1 ${W + 2} ${H + 2}`} className="h-9 w-14 shrink-0" role="img" aria-label={`${across} × ${deep}`}>
      <rect x="0" y="0" width={W} height={H} rx="1.5" fill="#f1f5f9" stroke="#94a3b8" strokeWidth="0.6" />
      {Array.from({ length: across * deep }, (_, i) => {
        const col = i % across;
        const row = Math.floor(i / across);
        const used = row * across + col < count;
        return <rect key={i} x={col * cellW + 0.6} y={row * cellH + 0.6} width={cellW - 1.2} height={cellH - 1.2} rx="0.8" fill={used ? '#d3a566' : 'none'} stroke={used ? '#8a5f30' : '#cbd5e1'} strokeWidth="0.5" strokeDasharray={used ? undefined : '1.5 1.5'} />;
      })}
    </svg>
  );
}

function PalletFront({ slot, selectedIndex, t }: { slot: PalletFrontSlot; selectedIndex: number | null; t: Props['t'] }) {
  const mixed = slot.shares.length > 1 || slot.shares[0].fraction < 0.995;
  const first = slot.shares[0];
  const dim = selectedIndex != null && !slot.shares.some((share) => share.index === selectedIndex);
  const selected = selectedIndex != null && !dim;
  const totalBoxes = !mixed && first.boxesPerLayer != null && first.layers != null ? Math.round(first.boxesPerLayer * first.layers) : null;
  const stats: string[] = mixed
    ? [t('supply.supplyAgent.loadPalletFrontMixed')]
    : [
      first.layers != null ? t('supply.supplyAgent.loadPalletFrontLayers', { count: Math.round(first.layers) }) : t('supply.supplyAgent.loadPalletFrontIllustrative'),
      first.boxesPerLayer != null ? t('supply.supplyAgent.loadPalletFrontPerLayer', { count: Math.round(first.boxesPerLayer) }) : '',
      totalBoxes != null ? t('supply.supplyAgent.loadPalletFrontBoxes', { count: totalBoxes }) : '',
      first.unitsPerPallet != null ? t('supply.supplyAgent.loadPalletFrontUnits', { count: Math.round(first.unitsPerPallet) }) : '',
    ].filter(Boolean);

  return (
    <figure className={`flex min-w-0 flex-col overflow-hidden rounded-xl border bg-white transition-opacity ${selected ? 'border-slate-900 ring-1 ring-slate-900' : 'border-slate-200'} ${dim ? 'opacity-40' : ''}`}>
      <div className="flex items-center gap-1.5 border-b border-slate-100 px-3 py-2">
        {mixed ? (
          <div className="flex min-w-0 flex-wrap gap-1">
            {slot.shares.map((share, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded-full bg-slate-100 py-0.5 pl-0.5 pr-2 text-[11px] font-medium text-slate-700" title={share.label}>
                <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-slate-800 px-1 text-[9px] font-bold text-white">{share.index ?? '·'}</span>
                {Math.round(share.fraction * 100)} %
              </span>
            ))}
          </div>
        ) : (
          <>
            <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-slate-800 px-1 text-[10px] font-bold text-white">{first.index ?? '·'}</span>
            <span className="truncate text-xs font-medium text-slate-800" title={first.label}>{first.label}</span>
          </>
        )}
      </div>
      <div className="bg-gradient-to-b from-slate-50 to-slate-100 px-3 pb-1 pt-3">
        <svg viewBox={palletFrontView(slot).viewBox} className="mx-auto max-h-60 w-full" role="img" aria-label={slot.shares.map((share) => share.label).join(' + ')}>
          <PalletFrontGraphic slot={slot} selectedIndex={selectedIndex} />
        </svg>
      </div>
      <figcaption className="space-y-1.5 border-t border-slate-100 px-3 py-2">
        <div className="flex flex-wrap gap-1">
          {stats.map((text) => <span key={text} className="rounded-md bg-slate-50 px-1.5 py-0.5 text-[11px] text-slate-600 ring-1 ring-slate-200">{text}</span>)}
        </div>
        {slot.shares.filter((share) => (share.boxesPerLayer ?? 0) > 0).map((share, i) => {
          const { across, deep } = arrangeLayer(share);
          return (
            <div key={i} className="flex items-center gap-2 text-[11px] text-slate-500">
              <LayerPlan share={share} />
              <span>
                {mixed && share.index != null ? <b className="mr-1 font-semibold text-slate-700">{share.index}</b> : null}
                {t('supply.supplyAgent.loadPalletFrontPlan', { boxes: Math.round(share.boxesPerLayer ?? 0), across, deep })}
              </span>
            </div>
          );
        })}
      </figcaption>
    </figure>
  );
}

export function PalletFrontGallery({ slots, selectedIndex, t }: Props) {
  if (slots.length === 0) return null;
  const shown = slots.slice(0, MAX_SLOTS);
  return (
    <div className="mt-4 border-t border-slate-200 px-4 pt-4">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-semibold text-slate-800">{t('supply.supplyAgent.loadPalletFrontTitle', { count: slots.length })}</p>
        <p className="text-[11px] text-slate-500">{t('supply.supplyAgent.loadPalletFrontHint')}</p>
      </div>
      <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(13rem,1fr))]">
        {shown.map((slot, index) => <PalletFront key={index} slot={slot} selectedIndex={selectedIndex} t={t} />)}
      </div>
      {slots.length > shown.length ? <p className="mt-2 text-xs text-slate-500">+{slots.length - shown.length}</p> : null}
    </div>
  );
}
